import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowUp,
  Bot,
  BriefcaseBusiness,
  Building2,
  CircleStop,
  Landmark,
  LoaderCircle,
  Mic,
  MicOff,
  RotateCcw,
  ShieldAlert,
  Sparkles,
  UserRound
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import type { AccountUser, CandidateProfile, Message, ServiceStatus } from "../types";

type Props = {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  profile: CandidateProfile;
  serviceStatus: ServiceStatus | null;
  user: AccountUser | null;
  credits: number | null;
  onRequireAuth: () => void;
  onRequireCredits: () => void;
  onCreditChanged: () => Promise<void>;
};

const suggestions = [
  {
    icon: BriefcaseBusiness,
    title: "专业值不值得学",
    prompt: "结合我的家庭条件和分数，帮我判断计算机、电子信息和自动化怎么排优先级。"
  },
  {
    icon: Building2,
    title: "学校还是城市",
    prompt: "同档次学校里，我应该优先学校层次、专业实力，还是城市？给我明确排序。"
  },
  {
    icon: Landmark,
    title: "冲稳保怎么分",
    prompt: "请根据我的档案，先讲清楚冲、稳、保的分配逻辑，以及最容易踩的坑。"
  }
];

export function ChatView({
  messages,
  setMessages,
  profile,
  serviceStatus,
  user,
  credits,
  onRequireAuth,
  onRequireCredits,
  onCreditChanged
}: Props) {
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [composerError, setComposerError] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const shouldFollowScrollRef = useRef(true);

  const appendSpeech = useCallback((text: string) => {
    setDraft((current) => (current.trim() ? `${current.trim()} ${text}` : text));
  }, []);
  const speech = useSpeechRecognition(appendSpeech);

  useEffect(() => {
    if (!shouldFollowScrollRef.current) return;
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const handleContentScroll = () => {
    const content = contentRef.current;
    if (!content) return;
    shouldFollowScrollRef.current =
      content.scrollHeight - content.scrollTop - content.clientHeight < 96;
  };

  const send = async (text = draft) => {
    const content = text.trim();
    if (!content || isSending) return;
    if (!user) {
      onRequireAuth();
      return;
    }
    if (credits === 0) {
      onRequireCredits();
      return;
    }
    if (!serviceStatus?.configured) {
      setComposerError("模型还没配置。先在项目根目录填写 .env，再重启服务。");
      return;
    }

    setComposerError("");
    setDraft("");
    setIsSending(true);
    shouldFollowScrollRef.current = true;
    const userMessage: Message = {
      id: createMessageId(),
      role: "user",
      content,
      createdAt: Date.now(),
      status: "done"
    };
    const assistantId = createMessageId();
    const requestId = createMessageId();
    const assistantMessage: Message = {
      id: assistantId,
      role: "assistant",
      content: "",
      createdAt: Date.now(),
      status: "streaming"
    };
    const requestMessages = [...messages, userMessage].map(({ role, content }) => ({
      role,
      content
    }));
    setMessages((current) => [...current, userMessage, assistantMessage]);

    const controller = new AbortController();
    abortRef.current = controller;
    let requestStarted = false;

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: requestMessages, profile, requestId }),
        signal: controller.signal
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (response.status === 401) onRequireAuth();
        if (response.status === 402) onRequireCredits();
        throw new Error(payload?.detail || payload?.error || `请求失败（${response.status}）`);
      }
      requestStarted = true;
      if (!response.body) throw new Error("服务没有返回可读取的内容");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);
          if (event.type === "delta") {
            setMessages((current) =>
              current.map((message) =>
                message.id === assistantId
                  ? { ...message, content: message.content + event.content }
                  : message
              )
            );
          }
          if (event.type === "error") throw new Error(event.error);
        }

        if (done) break;
      }

      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId ? { ...message, status: "done" } : message
        )
      );
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId
            ? {
                ...message,
                status: aborted ? "done" : "error",
                content:
                  message.content ||
                  (aborted
                    ? "已停止生成。"
                    : `这次没接上模型：${error instanceof Error ? error.message : "未知错误"}`)
              }
            : message
        )
      );
    } finally {
      abortRef.current = null;
      setIsSending(false);
      if (requestStarted) await onCreditChanged();
    }
  };

  const stop = () => abortRef.current?.abort();
  const clear = () => {
    abortRef.current?.abort();
    setMessages([]);
    setComposerError("");
  };

  return (
    <section className="chat-shell" aria-label="志愿咨询对话">
      <header className="chat-header">
        <div>
          <span className="eyebrow">决策咨询台</span>
          <h1>把选择说透，别拿四年试错</h1>
        </div>
        {messages.length > 0 && (
          <button className="ghost-button" type="button" onClick={clear}>
            <RotateCcw size={16} />
            新咨询
          </button>
        )}
      </header>

      <div className="boundary-note" role="note">
        <ShieldAlert size={18} aria-hidden="true" />
        <span>
          <strong>数据边界：</strong>
          具体分数线、招生计划与专业限制必须以省考试院和高校当年章程为准。
        </span>
      </div>

      <div
        ref={contentRef}
        className={messages.length === 0 ? "chat-content chat-content--empty" : "chat-content"}
        onScroll={handleContentScroll}
      >
        {messages.length === 0 ? (
          <Welcome onPick={send} />
        ) : (
          <div className="message-list" aria-live="polite">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            <div ref={listEndRef} />
          </div>
        )}
      </div>

      <div className="composer-wrap">
        {(composerError || speech.error) && (
          <p className="composer-error" role="alert">
            {composerError || speech.error}
          </p>
        )}
        <div className={speech.isListening ? "composer composer--listening" : "composer"}>
          <textarea
            aria-label="输入你的志愿填报问题"
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            placeholder={
              speech.isListening
                ? "正在听，你说……"
                : "直接问。比如：这个分数该保学校，还是保专业？"
            }
          />
          <div className="composer-actions">
            <button
              className={speech.isListening ? "icon-button icon-button--live" : "icon-button"}
              type="button"
              onPointerDown={(event) => {
                if (event.button !== 0 || !speech.isSupported || isSending) return;
                event.preventDefault();
                speech.start();
                const pointerId = event.pointerId;
                const finish = (releaseEvent: PointerEvent) => {
                  if (releaseEvent.pointerId !== pointerId) return;
                  window.removeEventListener("pointerup", finish);
                  window.removeEventListener("pointercancel", finish);
                  speech.stop();
                };
                window.addEventListener("pointerup", finish);
                window.addEventListener("pointercancel", finish);
              }}
              onKeyDown={(event) => {
                if (
                  !event.repeat &&
                  (event.key === " " || event.key === "Enter") &&
                  speech.isSupported &&
                  !isSending
                ) {
                  event.preventDefault();
                  speech.start();
                }
              }}
              onKeyUp={(event) => {
                if (event.key === " " || event.key === "Enter") {
                  event.preventDefault();
                  speech.stop();
                }
              }}
              onBlur={speech.stop}
              onContextMenu={(event) => event.preventDefault()}
              disabled={!speech.isSupported || isSending}
              aria-label={
                !speech.isSupported
                  ? "当前浏览器不支持语音输入"
                  : speech.isListening
                    ? "正在录音，松开停止"
                    : "按住录音，松开停止"
              }
              title={
                speech.isSupported ? "按住录音，松开停止" : "当前浏览器不支持 Web Speech API"
              }
            >
              {speech.isSupported ? <Mic size={20} /> : <MicOff size={20} />}
            </button>
            {isSending ? (
              <button className="send-button send-button--stop" type="button" onClick={stop}>
                <CircleStop size={19} />
                <span>停止</span>
              </button>
            ) : (
              <button
                className="send-button"
                type="button"
                onClick={() => void send()}
                disabled={!draft.trim()}
              >
                <span>发送</span>
                <ArrowUp size={19} />
              </button>
            )}
          </div>
        </div>
        <p className="composer-hint">
          {speech.isSupported ? "按住麦克风说话，松开停止" : "此浏览器不支持语音录入"} · Enter 发送，Shift +
          Enter 换行
        </p>
      </div>
    </section>
  );
}

function Welcome({ onPick }: { onPick: (prompt: string) => Promise<void> }) {
  return (
    <div className="welcome">
      <div className="welcome-mark" aria-hidden="true">
        <Sparkles size={24} />
      </div>
      <p className="welcome-kicker">先看家庭和位次，再谈热爱和名校</p>
      <h2>你不是缺学校名单，<br />你是缺一套取舍顺序。</h2>
      <p className="welcome-copy">
        左边把考生档案补齐。这里不许愿，不硬猜“能不能上”，只把就业、城市、学校和专业的账算明白。
      </p>
      <div className="suggestion-grid">
        {suggestions.map(({ icon: Icon, title, prompt }) => (
          <button key={title} className="suggestion-card" type="button" onClick={() => void onPick(prompt)}>
            <span className="suggestion-card__icon">
              <Icon size={19} />
            </span>
            <strong>{title}</strong>
            <span>{prompt}</span>
            <ArrowUp className="suggestion-card__arrow" size={17} />
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  return (
    <article className={`message message--${message.role}`}>
      <div className="message__avatar" aria-hidden="true">
        {message.role === "assistant" ? <Bot size={18} /> : <UserRound size={18} />}
      </div>
      <div className="message__body">
        <div className="message__meta">
          <strong>{message.role === "assistant" ? "志愿直说" : "你"}</strong>
          {message.status === "streaming" && (
            <span className="streaming-label">
              <LoaderCircle size={13} />
              正在拆解
            </span>
          )}
        </div>
        <div className="markdown">
          {message.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          ) : (
            <span className="typing-dots" aria-label="正在生成">
              <i />
              <i />
              <i />
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function createMessageId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
